import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, DownloadCloud, FolderTree, ChevronRight } from "lucide-react";
import { useApp } from "@/lib/app-context";
import { errMsg, dt } from "@/lib/api";
import { PageHeader, Panel, Field, NativeSelect, StatusBadge, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const BLANK = { name: "", parent_id: "", zepio_category_id: "", active: true };

function ImportPanel() {
  const { api, branches, can } = useApp();
  const mapped = branches.filter((b) => b.zepio_branch_id);
  const [f, setF] = useState({ branch_id: mapped[0]?.id || "", with_stock: true, update_existing: false });
  const [st, setSt] = useState(null);
  const load = () => api.get("/zepio/import/status").then((r) => setSt(r.data));
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async () => {
    if (!window.confirm("Import categories, products and stock from Zepio for this branch?")) return;
    try { await api.post("/zepio/import", f); toast.success("Import started"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Import from Zilabi online store (Zepio)" testid="zepio-import-panel">
      <p className="text-sm text-muted-foreground mb-3">Reads categories, subcategories, products, taxes and stock from Zepio (read-only on Zepio). Products are matched by erp_id, Zepio id or SKU so re-running never creates duplicates. Product variants are not imported.</p>
      {!mapped.length ? <p className="text-sm text-amber-700" data-testid="import-needs-mapping">Map a branch to a Zepio branch ID (Settings → Branches) and save the Zepio connection first.</p> : <>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <Field label="ERP branch"><NativeSelect testid="import-branch-select" value={f.branch_id} onChange={(v) => setF({ ...f, branch_id: v })} options={mapped.map((b) => ({ value: b.id, label: `${b.code} → Zepio #${b.zepio_branch_id}` }))} /></Field>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.with_stock} onCheckedChange={(v) => setF({ ...f, with_stock: v })} data-testid="import-with-stock-switch" />Zepio stock as opening stock</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.update_existing} onCheckedChange={(v) => setF({ ...f, update_existing: v })} data-testid="import-update-existing-switch" />Overwrite existing ERP products</label>
        </div>
        {can("settings") && <Button className="mt-3" onClick={run} disabled={st?.status === "running"} data-testid="start-import-button"><DownloadCloud className="h-4 w-4 mr-1" />{st?.status === "running" ? "Importing…" : "Import now"}</Button>}
      </>}
      {st && st.status !== "never" && <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm" data-testid="import-status">
        <div className="flex items-center gap-2"><StatusBadge s={st.status} /> <span className="text-xs text-muted-foreground">started {dt(st.started_at)}{st.finished_at && ` · finished ${dt(st.finished_at)}`}</span></div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-xs">
          <span>Categories created <b>{st.categories_created}</b></span><span>Categories linked <b>{st.categories_linked}</b></span>
          <span>Products created <b>{st.products_created}</b></span><span>Updated <b>{st.products_updated}</b> · skipped <b>{st.products_skipped}</b></span>
          <span>Opening stock batches <b>{st.stock_batches}</b></span><span>Variants skipped <b>{st.variants_skipped}</b></span></div>
        {st.errors?.length > 0 && <div className="mt-2 text-xs text-red-600 max-h-28 overflow-y-auto">{st.errors.map((e, i) => <div key={i}>{e}</div>)}</div>}
      </div>}
    </Panel>
  );
}

export default function Catalogue() {
  const { api, can } = useApp();
  const [cats, setCats] = useState([]);
  const [f, setF] = useState(null);
  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    const body = { ...BLANK, ...f, parent_id: f.parent_id || null, zepio_category_id: f.zepio_category_id ? Number(f.zepio_category_id) : null, zepio_category_ids: f.zepio_category_ids || [] };
    try { f.id ? await api.put(`/categories/${f.id}`, body) : await api.post("/categories", body); toast.success("Category saved"); setF(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const tops = cats.filter((c) => !c.parent_id);
  const kids = (id) => cats.filter((c) => c.parent_id === id);
  const Row = ({ c, depth }) => (
    <>
      <div className="flex items-center gap-2 border-t py-2 text-sm" style={{ paddingLeft: depth * 24 }} data-testid={`category-row-${c.name}`}>
        {depth > 0 && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
        <span className={`font-medium ${depth ? "" : "font-semibold"}`}>{c.name}</span>
        {c.active === false && <StatusBadge s="disabled" />}
        <span className="ml-auto flex items-center gap-2">
          {c.zepio_category_id ? <span className="text-xs text-emerald-700">Website #{c.zepio_category_id}</span> : <span className="text-xs text-amber-700" title="Zepio has no category create/update API">Not on website · awaiting API</span>}
          {can("products.edit") && <Button size="sm" variant="ghost" onClick={() => setF({ ...c, zepio_category_id: c.zepio_category_id ?? "" })} data-testid={`edit-category-${c.name}`}>Edit</Button>}
        </span>
      </div>
      {kids(c.id).map((k) => <Row key={k.id} c={k} depth={depth + 1} />)}
    </>
  );
  return (
    <div>
      <PageHeader title="Catalogue" subtitle="Categories, subcategories and website catalogue import">
        {can("products.edit") && <Button onClick={() => setF({ ...BLANK })} data-testid="add-category-button"><Plus className="h-4 w-4 mr-1" />Category</Button>}
      </PageHeader>
      <div className="grid lg:grid-cols-5 gap-4">
        <Panel title={<span className="flex items-center gap-2"><FolderTree className="h-4 w-4 text-emerald-600" />Category tree</span>} className="lg:col-span-3">
          {f && <div className="rounded-lg border bg-emerald-50/50 p-3 mb-3 grid sm:grid-cols-4 gap-3 items-end">
            <Field label="Name"><Input data-testid="category-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
            <Field label="Parent (blank = top level)"><NativeSelect testid="category-parent-select" value={f.parent_id || ""} onChange={(v) => setF({ ...f, parent_id: v })} placeholder="— Top level —" options={cats.filter((c) => c.id !== f.id).map((c) => ({ value: c.id, label: c.name }))} /></Field>
            <Field label="Zepio category ID (link)"><Input data-testid="category-zepio-id-input" type="number" value={f.zepio_category_id} onChange={(e) => setF({ ...f, zepio_category_id: e.target.value })} /></Field>
            <div className="flex gap-2"><Button size="sm" onClick={save} data-testid="save-category-button">Save</Button><Button size="sm" variant="ghost" onClick={() => setF(null)}>Cancel</Button></div>
          </div>}
          {tops.length ? tops.map((c) => <Row key={c.id} c={c} depth={0} />) : <Empty text="No categories" />}
          <p className="text-xs text-muted-foreground mt-3">Products sync their category to the website (Zepio <code>categories</code>) only when you change a product's category and that category is linked. Zepio's API cannot create or rename website categories, so new ERP categories must also be created in the Zepio admin and linked here by ID.</p>
        </Panel>
        <div className="lg:col-span-2"><ImportPanel /></div>
      </div>
    </div>
  );
}
